export default {
	// The rules of the dialogs that are about the domain. Everything generic - `required`,
	// `tooLong`, `invalidEmail` and the other rules of lib/js/utils/validations.ts - comes from
	// the lib; `tooManyLines` is here only because the number says more than "too many".
	validations: {
		died_before_born: 'Date of death is before the date of birth',
		share_incomplete: 'Numerator and denominator belong together',
		too_many_lines: 'At most six lines',
		key_format: 'Lower case letters, digits and _ only',
	},

	// Labels of the filter components under resources/js/components/filter/, which sit above
	// more than one screen and belong to none of them.
	filters: {
		location: 'Location',
		location_none: 'Without location',
		tag: 'Tag',
	},

	sidebar: {
		dashboard: 'Dashboard',
		pipeline: 'Onboarding',
		law_firms: 'Law firms',
		matters: 'Cases',
		tasks: 'Tasks',
		calendar: 'Calendar',
		// The shared notebook. The tab labels of the per record ones are under tabs.*.
		notes: 'Notes',
		crm_header: 'Clients',
		work_header: 'Case work',
		admin_header: 'Administration',
		users: 'Users',
		locations: 'Locations',
		correspondence_templates: 'Letter templates',
		tags: 'Tags',
	},

	tabs: {
		law_firm: {
			overview: 'Overview',
			onboarding: 'Onboarding',
			tasks: 'Tasks',
			contacts: 'Contacts',
			matters: 'Cases',
			communications: 'Correspondence',
			log: 'Contact log',
			notes: 'Notes',
		},
		matter: {
			overview: 'Overview',
			tree: 'Family tree',
			tasks: 'Tasks',
			documents: 'Files',
			log: 'Contact log',
			notes: 'Notes',
		},
	},

	buttons: {
		task: {
			create: 'Create task',
			edit: 'Edit task',
			delete: 'Delete task',
			complete: 'Mark as done',
			reopen: 'Reopen',
		},
		law_firm: {
			create: 'Create law firm',
			edit: 'Edit law firm',
			delete: 'Delete law firm',
			open: 'Open law firm',
		},
		contact: {
			create: 'Create contact',
			edit: 'Edit contact',
			delete: 'Delete contact',
		},
		location: {
			create: 'Create location',
			edit: 'Edit location',
			delete: 'Delete location',
		},
		matter: {
			create: 'Create case',
			edit: 'Edit case',
			delete: 'Delete case',
			open: 'Open case',
			close: 'Close case',
			reopen: 'Reopen case',
		},
		person: {
			create: 'Create person',
			edit: 'Edit person',
			delete: 'Delete person',
			add_parent: 'Link a parent',
			remove_parent: 'Remove link',
		},
		document: {
			create: 'Upload file',
			edit: 'Edit file',
			delete: 'Delete file',
			open: 'Open file',
			preview: 'View file',
		},
		interaction: {
			create: 'Add entry',
			edit: 'Edit entry',
			delete: 'Delete entry',
		},
		correspondence_template: {
			create: 'Add template',
			edit: 'Edit template',
			delete: 'Delete template',
		},
		tag: {
			create: 'Add tag',
			edit: 'Edit tag',
			delete: 'Delete tag',
		},
		communication: {
			create: 'Write a letter',
			edit: 'Edit letter',
			delete: 'Delete letter',
			generate: 'Draft from template',
			generate_outreach: 'Draft the first letter',
			view: 'View',
			print: 'Print',
			pdf: 'Download as PDF',
			// The print view's toolbar, the long label as its hint
			review: 'Approve',
			mark_sent: 'Record as sent',
			mark_sent_short: 'Sent',
			download_short: 'Download',
			unsend: 'Undo sending',
		},
		tree: {
			zoom_in: 'Zoom in',
			zoom_out: 'Zoom out',
			reset: 'Reset the view',
			print: 'Print chart',
		},
		onboarding: {
			create: 'Add step',
			toggle: 'Tick step',
		},
	},

	pages: {
		dashboard: {
			title: 'Dashboard',
			pipeline: 'Onboarding pipeline',
			my_tasks: 'My tasks',
			follow_ups: 'Follow ups',
			deadlines: 'Deadlines',
			my_matters: 'My cases',
			matters_by_state: 'Cases by state',
			empty: 'Nothing open.',
		},
		tasks: {
			title: 'Tasks',
			subtitle: 'Every task, filtered by owner and state.',
			mine: 'Assigned to me',
			all: 'All',
			all_states: 'All',
			open: 'Open',
			done: 'Done',
			overdue: 'Overdue',
			unassigned: 'Unassigned',
			no_subject: 'No reference',
			empty: 'No tasks.',
			empty_open: 'Nothing open.',
			completed_by: 'Completed by {name} on {date}',
		},
		communication_print: {
			subject_label: 'Subject:',
			archived: 'Archived version of {date}',
			not_archived: 'No archived version - shown on today\'s letterhead',
		},
		correspondence_templates: {
			title: 'Letter templates',
			subtitle: 'The templates a draft in the correspondence of a firm is written from.',
			empty: 'No template yet.',
			hint: 'A draft takes the subject, text and PS of its template; address, salutation and letterhead come from the firm. A change here applies to new drafts, not to letters already written.',
			locked: 'The first letter is used when a firm is created and by the import, and cannot be deleted.',
		},
		tags: {
			title: 'Tags',
			subtitle: 'What a law firm is marked with: a filter in the list, a pill on the board.',
			empty: 'No tag yet.',
			failed: 'The tags could not be loaded.',
			hint: 'The research import sets tags by their key and decides by the description when one applies. The key is made from the name when none is given, and can be changed.',
		},
		locations: {
			title: 'Locations',
			subtitle: 'The proERBE offices. A location is picked on a law firm.',
			empty: 'No location yet.',
			hint: "A firm's location also holds for its cases and decides the sender address of its letters. A location that firms still point at cannot be deleted.",
			preview: 'Letterhead of this location',
			preview_hint:
				'The line in the address field and the town in front of the date come from this location, everything else from the shared letterhead.',
		},
		letterhead: {
			title: 'Letterhead',
			location_name: 'Name of the location',
			return_line_hint: 'The small line above the address field, the one a window envelope shows. The Briefpapier puts it in as {placeholder} and can bring every office into one form there.',
			place_hint: 'The town in the date line.',
			phone_hint: "The phone number in the contact block at the top right. Left empty, the main office's number stands there.",
			sample_recipient: 'Kanzlei Muster & Partner\nMusterstraße 1\n47533 Kleve',
			sample_subject: 'Working together on heir research',
			sample_salutation: 'Sehr geehrte Damen und Herren,',
			sample_body:
				"This is what a letter looks like on this letterhead. The text comes from the firm's correspondence, everything else on the page is set here.",
			sample_closing: 'Mit freundlichen Grüßen',
		},
		calendar: {
			title: 'Calendar',
		},
		law_firms: {
			title: 'Law firms',
			pipeline_title: 'Onboarding pipeline',
			board: 'Pipeline',
			list: 'List',
			closed_hint: '{count} firms are dormant or lost and are not shown on the board.',
			drop_hint: 'Drag a card into another column to change its state.',
			backlog_hint:
				'Deferred on purpose, and due again by itself through the follow up date. Not the same as dormant, where the contact has ended.',
			required_open: 'There are still required steps open.',
		},
		law_firm: {
			title: 'Law firm',
			delete_blocked: 'A firm that carries cases cannot be deleted - set it dormant instead.',
			master_data: 'Master data',
			address: 'Address',
			contact: 'Contact',
			pipeline: 'Pipeline',
			progress: 'Onboarding: {done} of {total}',
			no_contacts: 'No contact person recorded yet.',
			no_matters: 'No case has been created for this firm yet.',
			no_communications: 'No letter to this firm yet.',
			communications_failed: 'The correspondence could not be loaded.',
			date_sent: 'sent',
			date_created: 'created',
			communication_locked: 'Sent - editing and deleting locked',
		},
		matters: {
			title: 'Cases',
			open_only: 'Open only',
		},
		matter: {
			title: 'Case',
			delete_blocked:
				'Only an open case with no files and no person beside its decedent can be deleted - any other is closed instead.',
			decedent: 'Decedent',
			court: 'Probate court',
			fee: 'Fee',
			dates: 'Dates',
			closed_hint: 'This case is closed and read only.',
			no_log: 'No contact recorded yet.',
			dropzone: {
				hint: 'Drop files here',
				browse: 'or click to pick files',
				uploading: 'Uploading files',
				filed_as: 'Filed as: {category}',
				too_large: 'Larger than {size}',
				failed: 'Upload failed',
				retry: 'Retry | Retry one upload | Retry {count} uploads',
				clear: 'Clear the list',
			},
		},
		family_tree: {
			title: 'Family tree',
			delete_blocked: 'The decedent cannot be deleted - pick another decedent on the case first.',
			generation: 'Generation {n}',
			unplaced: 'Unplaced',
			unplaced_hint: 'These persons are not attached to any branch of the decedent.',
			no_decedent: 'This case has no decedent set.',
			legend: 'Legend',
			share: 'Share',
			relation: 'Relation',
			select_parent: 'Select a parent',
			no_selection: 'Select a person in the tree to see her details, tasks and files.',
			no_documents: 'No file is filed under this person.',
			cyclic: 'That link would create a cycle.',
		},
		heir_chart: {
			title: 'Heir chart',
			in_the_estate_of: 'In the estate of {name}',
			reference: 'Case reference',
			decedent: 'Decedent',
			court: 'Probate court',
			court_reference: 'Court reference',
			born_on: 'born {date}',
			died_on: 'died {date}',
			place_and_date: '{place}, {date}',
			born_name: '(born {name})',
			dashed_hint: 'Dashed: adoptive, step or unclear descent',
			register: 'Register of parties',
			column_generation: 'Gen.',
			column_name: 'Name',
			column_state: 'Research state',
			footnote: 'The shares are entered by hand, not calculated.',
		},
	},

	modals: {
		law_firm: {
			create: 'Create law firm',
			edit: 'Edit law firm',
			location_hint: "Also holds for this firm's cases, and decides the sender address of its letters.",
			section_origin: 'Origin and note',
			section_create: 'On create',
		},
		location: {
			create: 'Create location',
			edit: 'Edit location',
			letterhead_hint:
				'The letterhead of this office. These four fields are the whole of what differs between the locations, everything else is under Administration > Letterhead.',
		},
		contact: {
			create: 'Create contact',
			edit: 'Edit contact',
		},
		matter: {
			create: 'Create case',
			edit: 'Edit case',
			decedent_hint: 'The decedent is created as the root of the family tree.',
			decedent_edit_hint: 'The same person as in the family tree - a correction here shows there too.',
			decedent_select_hint:
				'The decedent is the root of the family tree and is moved only here - it renumbers every generation.',
			decedent_none: 'The family tree holds no persons a decedent could be picked from yet.',
			close: {
				text: '"{item}" is closed and locked for editing. It can be reopened at any time.',
			},
		},
		person: {
			create: 'Create person',
			edit: 'Edit person',
			section_relation: 'Relation and share',
		},
		document: {
			create: 'Upload file',
			edit: 'Edit file',
			preview: {
				close: 'Close',
				open_tab: 'Open in a new tab',
				download: 'Download',
				not_previewable: 'This format cannot be displayed, only downloaded.',
				no_file: 'No file is stored for this record.',
			},
		},
		interaction: {
			create: 'Add contact log entry',
			edit: 'Edit contact log entry',
		},
		correspondence_template: {
			create: 'Add template',
			edit: 'Edit template',
			once_hint: 'At most one draft per firm, like the first letter.',
			active_hint: 'Only active templates are offered in the correspondence.',
			locked_hint: 'The Anschreiben stays active and once per firm: the law firm form and the research import write it by its key. Text and subject are yours.',
			section_text: 'Letter text',
			text_hint:
				'Separate paragraphs by a blank line. Salutation and closing are added per firm, the first paragraph continues the salutation.',
			gap_hint:
				'shows in red in the draft and has to be replaced or deleted before sending - also for paragraphs that only sometimes fit, like "Only with a foreign estate - otherwise delete the paragraph".',
			placeholders: {
				firm: 'Name of the firm',
				city: 'City of the firm',
				office: 'Location',
				salutation: 'Salutation, per contact',
				date: 'The date of the day',
				note: 'Personal reference, otherwise a red gap',
			},
		},
		tag: {
			create: 'Add tag',
			edit: 'Edit tag',
			remove: {
				title: 'Delete the tag?',
				text: '"{item}" is deleted. | "{item}" is deleted and taken off one law firm. | "{item}" is deleted and taken off {count} law firms.',
			},
		},
		communication: {
			create: 'Write a letter',
			edit: 'Edit letter',
			section_letter: 'Letter text',
			section_blocks: 'Recipient and sender',
			blocks_hint:
				'Stored for this letter alone - a correction here changes neither the firm nor the letterhead.',
			outreach_hint:
				'Drafts the first outreach letter from the template. It is a draft and wants reading before it goes out.',
			generate_hint: 'Drafts a letter from a template. It is a draft and wants reading before it goes out.',
			generate_used: 'This template is used once per firm and has already been drafted.',
			mark_sent: {
				title: 'Record as sent?',
				text: '"{item}" becomes read only and is filed in the contact log.',
			},
			review: {
				title: 'Approve the letter?',
				text: 'Has "{item}" been read and is everything in order? It is then approved and can be sent.',
			},
			review_gaps: {
				title: 'Placeholders left in the letter',
				text: '"{item}" still contains placeholders and suggested wording. Fill them in first, then approve.',
			},
			reviewed_hint: 'Approved. A change to the text takes it back to draft, and it has to be approved again.',
			closing_placeholder: 'Empty: the closing of the letterhead',
		},
		task: {
			create: 'Create task',
			edit: 'Edit task',
			outreach_hint: 'Creates the task "write to the firm" when saved.',
		},
		remove: {
			title: 'Really delete?',
			text: '"{item}" will be removed permanently.',
		},
	},

	data: {
		task: {
			id: 'No.',
			title: 'Task',
			description: 'Description',
			kind: 'Kind',
			due_at: 'Due',
			completed_at: 'Completed',
			assigned_to: 'Assignee',
			subject: 'Reference',
			state: 'State',
		},
		calendar_event: {
			task: 'Task',
			follow_up: 'Follow up',
			matter_deadline: 'Case deadline',
			onboarding_step: 'Onboarding step',
		},
		law_firm: {
			id: 'No.',
			name: 'Name',
			type: 'Type',
			priority: 'Priority',
			location: 'Location',
			location_none: 'No location',
			tags: 'Tags',
			city: 'City',
			zip: 'ZIP',
			street: 'Street',
			street_number: 'No.',
			country: 'Country',
			phone: 'Phone',
			email: 'Email',
			website: 'Website',
			onboarding_state: 'State',
			follow_up_at: 'Follow up',
			source: 'Source',
			summary: 'Note',
			owner: 'Owner',
			matters_count: 'Cases',
			onboarding_progress: 'Onboarding',
		},
		contact: {
			salutation: 'Salutation',
			title: 'Title',
			first_name: 'First name',
			last_name: 'Last name',
			role: 'Role',
			email: 'Email',
			phone: 'Phone',
			mobile: 'Mobile',
			is_primary: 'Primary',
			remark: 'Remark',
		},
		matter: {
			id: 'No.',
			reference: 'Reference',
			title: 'Title',
			law_firm: 'Law firm',
			location: 'Location',
			decedent: 'Decedent',
			court_name: 'Probate court',
			court_reference: 'Court reference',
			state: 'State',
			opened_at: 'Opened',
			deadline_at: 'Deadline',
			closed_at: 'Closed',
			fee_model: 'Fee model',
			fee_value: 'Fee value',
			summary: 'Summary',
			assigned_to: 'Assignee',
			persons_count: 'Persons',
			documents_count: 'Files',
		},
		person: {
			first_name: 'First name',
			last_name: 'Last name',
			birth_name: 'Birth name',
			gender: 'Gender',
			born_at: 'Born',
			born_place: 'Place of birth',
			died_at: 'Died',
			died_place: 'Place of death',
			last_residence: 'Last residence',
			life_state: 'Life state',
			research_state: 'Research state',
			relation_label: 'Relation to the decedent',
			share: 'Share',
			share_numerator: 'Numerator',
			share_denominator: 'Denominator',
			remark: 'Remark',
			parents: 'Parents',
			children: 'Children',
		},
		document: {
			title: 'Title',
			category: 'Category',
			document_date: 'Date',
			issuer: 'Issued by',
			person: 'Person',
			no_person: 'Without a person',
			uploaded_by: 'Uploaded by',
			created_at: 'Uploaded at',
			size: 'Size',
			remark: 'Remark',
			file: 'File',
		},
		interaction: {
			happened_at: 'Date',
			kind: 'Kind',
			subject: 'Subject',
			body: 'Text',
			user: 'Recorded by',
			contact: 'Contact',
		},
		location: {
			name: 'Location',
			is_default: 'Main office',
			law_firms_count: 'Law firms',
		},
		letterhead: {
			contact_title: 'Name of the office',
			contact_address: 'Address (one line each)',
			return_line: 'Return line',
			place: 'Place',
			phone: 'Phone',
			location_name: 'Name of the location',
			groups: {
				data: 'Location',
			},
		},
		correspondence_template: {
			name: 'Name',
			description: 'Description',
			channel: 'Channel',
			subject: 'Subject',
			body: 'Text',
			postscript: 'PS',
			once_per_firm: 'Once per firm',
			is_active: 'Active',
			sort: 'Order',
		},
		tag: {
			key: 'Key',
			name: 'Name',
			color: 'Colour',
			description: 'Description',
			sort: 'Order',
			law_firms_count: 'Law firms',
		},
		communication: {
			channel: 'Channel',
			state: 'State',
			subject: 'Subject',
			salutation: 'Salutation',
			body: 'Text',
			closing: 'Closing',
			postscript: 'Postscript',
			text_size: 'Letter text size',
			recipient_block: 'Recipient',
			sender_block: 'Sender',
			contact: 'Contact',
			date: 'Date',
			user: 'Written by',
		},
		onboarding_step: {
			label: 'Step',
			is_required: 'Required',
			due_at: 'Due',
			completed_at: 'Done',
			completed_by: 'Done by',
			remark: 'Remark',
		},
		enum: {
			law_firm_priority: {
				a: 'A',
				b: 'B',
				c: 'C',
				none: 'None',
			},
			law_firm_type: {
				law_firm: 'Law firm',
				estate_administrator: 'Estate administrator',
				probate_court: 'Probate court',
				notary: 'Notary',
				other: 'Other',
			},
			onboarding_state: {
				new: 'New',
				lead: 'Lead',
				contacted: 'Contacted',
				meeting: 'Meeting',
				agreement: 'Agreement',
				active: 'Active',
				backlog: 'Backlog',
				dormant: 'Dormant',
				lost: 'Lost',
			},
			matter_state: {
				intake: 'Intake',
				research: 'Research',
				evidence: 'Evidence',
				negotiation: 'Negotiation',
				settlement: 'Settlement',
				closed: 'Closed',
				aborted: 'Aborted',
			},
			fee_model: {
				quota: 'Success quota',
				hourly: 'Hourly rate',
				flat: 'Flat fee',
				mixed: 'Mixed',
			},
			life_state: {
				alive: 'Alive',
				deceased: 'Deceased',
				unknown: 'Unknown',
			},
			research_state: {
				assumed: 'Assumed',
				researching: 'Researching',
				confirmed: 'Confirmed',
				excluded: 'Excluded',
				unreachable: 'Unreachable',
			},
			relation_kind: {
				biological: 'Biological',
				adoptive: 'Adoptive',
				step: 'Step',
				unknown: 'Unknown',
			},
			union_kind: {
				marriage: 'Marriage',
				civil_partnership: 'Civil partnership',
				unmarried: 'Unmarried',
			},
			document_category: {
				certificate: 'Certificate',
				correspondence: 'Correspondence',
				evidence: 'Evidence',
				court: 'Court',
				internal: 'Internal',
				other: 'Other',
			},
			task_kind: {
				manual: 'Task',
				outreach: 'Outreach',
				triage: 'Triage',
				follow_up: 'Follow up',
				research: 'Research',
				document: 'Document',
			},
			interaction_kind: {
				call: 'Call',
				email: 'Email',
				letter: 'Letter',
				meeting: 'Meeting',
				internal: 'Internal',
			},
			communication_channel: {
				letter: 'Letter',
				email: 'Email',
			},
			letter_text_size: {
				normal: 'Normal (10.5 pt)',
				compact: 'Compact (10 pt)',
				small: 'Small (9.5 pt)',
			},
			communication_state: {
				draft: 'Draft',
				ready: 'Approved',
				sent: 'Sent',
			},
		},
	},
};
